import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as authApi from '../api/auth';
import { getMyTeamContext } from '../api/teams';
import { getTokenFromStorage, storeToken, clearTokens } from '../api/client';
import * as securityLogger from '../utils/securityLogger';
import { translate } from '../i18n/translate';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

// Account roles that own courses. Without a team of their own they resolve to
// the MENTOR functional role (courses, students, analytics) rather than STAFF.
const COURSE_OWNER_ACCOUNTS = ['MENTOR', 'TEACHER'];

/**
 * Parse JWT token to extract payload
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Get remaining time until token expires in seconds
 */
function getTokenTimeRemaining(token) {
  if (!token) return 0;
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return 0;
  const expiresAt = payload.exp * 1000;
  const remaining = Math.max(0, (expiresAt - Date.now()) / 1000);
  return remaining;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [staffRole, setStaffRole] = useState(null); // effective CRM functional role
  const [isLoading, setIsLoading] = useState(true);
  const [expirationWarningShown, setExpirationWarningShown] = useState(false);
  const [tokenTimeRemaining, setTokenTimeRemaining] = useState(null);

  // Resolve the effective CRM role. ADMIN accounts stay ADMIN; other staff get a
  // role from their team (via GET /teams/me).
  // SECURITY: fail closed. If /teams/me errors we must NOT grant full access — a
  // non-admin whose role can't be resolved falls back to the least-privilege
  // STAFF role, never ADMIN. (Real admins are handled by the account-role branch
  // above and never reach this catch.) This matters once external client-org
  // users can log into the CRM. See B2B_CRM_ROADMAP.md Phase 0.1.
  // A course owner (MENTOR / TEACHER account) is not unknown: with no team role
  // of its own, or when /teams/me cannot be read, it resolves to MENTOR, a role
  // that adds nothing the account role does not already allow on the API.
  // Resolves to the role it set, so login can pick the role's landing page.
  const resolveStaffRole = useCallback(async (u) => {
    const acct = String(u?.role || '').toUpperCase();
    if (acct === 'ADMIN' || acct === 'SUPERADMIN' || acct === 'SUPER_ADMIN') {
      setStaffRole('ADMIN');
      return 'ADMIN';
    }
    const noTeamRole = COURSE_OWNER_ACCOUNTS.includes(acct) ? 'MENTOR' : 'STAFF';
    let role = noTeamRole;
    try {
      const ctx = await getMyTeamContext();
      const teamRole = String(ctx?.role || 'STAFF').toUpperCase();
      role = teamRole === 'STAFF' ? noTeamRole : teamRole;
    } catch {
      // fail closed (see above)
    }
    setStaffRole(role);
    return role;
  }, []);

  const restoreSession = useCallback(async () => {
    // SECURITY: Use getTokenFromStorage which checks sessionStorage first
    const token = getTokenFromStorage('accessToken');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch fresh user profile from API (most secure approach)
      const profile = await authApi.getProfile();
      setUser(profile);
      resolveStaffRole(profile);
      // SECURITY: Only store essential user info in React state, NOT in localStorage
      // Sensitive user data stays in memory and is cleared on session end
    } catch (err) {
      const code = err?.code ?? err?.response?.status;
      const stillHasToken = Boolean(getTokenFromStorage('accessToken'));
      if (code === 401 || code === 403 || !stillHasToken) {
        // The session really is invalid — end it.
        clearTokens();
        setUser(null);
      } else {
        // Transient failure (rate limit, 5xx, network blip) while restoring:
        // don't throw the user out of the app. Fall back to the identity
        // carried in the (still valid) JWT so protected routes can render;
        // the next successful request refreshes the full profile.
        const claims = parseJwt(getTokenFromStorage('accessToken')) || {};
        const fallback = {
          id: claims.sub,
          email: claims.email || null,
          phone: claims.phone || null,
          name: claims.name || (claims.email ? claims.email.split('@')[0] : 'User'),
          role: claims.role,
          avatar: null,
          tenantId: claims.tenantId,
        };
        setUser(fallback);
        resolveStaffRole(fallback);
        authApi.getProfile()
          .then((profile) => { setUser(profile); resolveStaffRole(profile); })
          .catch(() => { /* keep the JWT-derived identity */ });
      }
    } finally {
      setIsLoading(false);
    }
  }, [resolveStaffRole]);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // Monitor token expiration and show warning
  useEffect(() => {
    const checkExpiration = () => {
      // Read the token every time: the API client swaps it when it refreshes.
      const token = getTokenFromStorage('accessToken');
      if (!token) {
        setTokenTimeRemaining(null);
        return;
      }
      const remaining = getTokenTimeRemaining(token);
      setTokenTimeRemaining(remaining);

      // Warn when 2 minutes remain, but only if the session cannot renew itself:
      // with a refresh token the API client replaces the access token on its own,
      // so that warning would be a false alarm every few minutes.
      if (remaining > 0 && remaining < 120 && !getTokenFromStorage('refreshToken') && !expirationWarningShown) {
        setExpirationWarningShown(true);
        toast.error(translate('shell.session.expiring'), { duration: 6000 });
      }

      // Reset warning flag when token is refreshed (time jumps forward)
      if (remaining > 300) {
        setExpirationWarningShown(false);
      }
    };

    checkExpiration();

    // Check every 30 seconds
    const interval = setInterval(checkExpiration, 30000);
    return () => clearInterval(interval);
  }, [expirationWarningShown]);

  const login = useCallback(async (identifier, password) => {
    const res = await authApi.login({ identifier, password });

    // SECURITY: Store tokens in sessionStorage (XSS-resistant) using secure storeToken function
    storeToken('accessToken', res.accessToken);
    storeToken('refreshToken', res.refreshToken);

    // SECURITY: Keep user data in React state only, not in storage
    // This prevents XSS attacks from reading user data from localStorage
    setUser(res.user);
    // Waited for: the login page sends the user to this role's landing page.
    const staffRole = await resolveStaffRole(res.user);

    // SECURITY: Log successful login
    securityLogger.logLogin(identifier);

    return { ...res, staffRole };
  }, [resolveStaffRole]);

  const logout = useCallback(() => {
    // SECURITY: Clear all sensitive data including tokens and user info
    clearTokens();
    setUser(null);
    setStaffRole(null);

    // SECURITY: Log logout event
    securityLogger.logLogout();

    window.location.href = '/login';
  }, []);

  // Merge fields the user just changed on their own profile (name, avatar) into
  // the signed-in identity so the shell shows them without another round trip.
  const updateUser = useCallback((changes) => {
    setUser((current) => (current ? { ...current, ...changes } : current));
  }, []);

  const value = {
    user,
    staffRole, // effective CRM functional role: ADMIN | SALES | CALL_CENTER | CONTROL | ACCOUNT_MANAGER | MENTOR | STAFF (null while resolving)
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    updateUser,
    restoreSession,
    tokenTimeRemaining, // Remaining seconds until token expires
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
